import {
  DeleteConfirm,
  IconButton,
  ReferenceArea,
  ReferenceField,
  SectionFooter,
} from '@/components/admin/reglages/reference-list';
import { Callout } from '@/components/ui/callout';
import { levelDeletionSentence } from '@/lib/persona-labels';
import type { ScaleRow } from '@/lib/persona-settings';
import {
  moveRow,
  newScaleRow,
  scaleBlocked,
  scaleDirty,
  scaleRowsOf,
} from '@/lib/persona-settings';
import type { InvolvementLevel } from '@matter_extractor/shared';
import {
  INVOLVEMENT_LEVELS_MIN,
  INVOLVEMENT_LEVEL_DIRECTION_MAX,
  INVOLVEMENT_LEVEL_NAME_MAX,
} from '@matter_extractor/shared';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { useId, useState } from 'react';

/**
 * Section 1 of Réglages > Personas: the involvement scale (§11.5, planche
 * `ReglagesPersonas`).
 *
 * The crans a persona's slider walks. NOT versioned, and the screen says so
 * itself rather than leaving it to the spec: the save OVERWRITES, and there is
 * no history to walk back.
 *
 * ## It owns its draft, and the route REMOUNTS it
 *
 * The whole section is edited then saved d'un bloc, so the rows live in state
 * here and are initialised from the props - never by an effect. The route
 * mounts this component with a `key` it bumps after every write that reached
 * the base, exactly as `criteres.$criterionId` does: « recharger » is a
 * REMOUNT, and a remount cannot half-apply.
 *
 * ## What is immediate, and what waits for the button
 *
 * A DELETION is immediate: it has its own confirmation, it counts the personas
 * it touches, and the base re-densifies the positions behind it - three things
 * that would be a lie if they sat in a pending block. Everything else - the
 * names, the directions, the order, the additions - waits for « Enregistrer
 * l'échelle », which is what the planche draws.
 *
 * Which is why the confirmation SAYS SO when the block is dirty: a deletion
 * re-reads the section, so the edits waiting on the button go with it, and an
 * operator about to lose four thousand characters of direction deserves the
 * sentence rather than the surprise.
 *
 * ## Arrows rather than a drag handle
 *
 * The planche draws a grip. Two arrow buttons carry the same gesture, work from
 * the keyboard, need no drag library, and can NAME what they do to a screen
 * reader (« Descendre le cran "en retrait" ») where a handle names nothing.
 * A handle can replace them the day the drag is worth its dependency; the
 * order it produces is the same request either way.
 */

export interface InvolvementScaleProps {
  /** The scale as the base holds it. The draft below starts from it. */
  readonly levels: readonly InvolvementLevel[];
  readonly saving: boolean;
  /** The cran being deleted right now, so its own row can say so. */
  readonly deletingId: string | null;
  /** The refusal, worded by the caller from the server's `code`. */
  readonly error: string | null;
  readonly onSave: (rows: readonly ScaleRow[]) => void;
  readonly onDelete: (id: string) => void;
}

/** The 36 px the trash occupies, reserved so every field ends at the same x. */
function Gutter() {
  return <span aria-hidden="true" className="block w-9 shrink-0" />;
}

export function InvolvementScale({
  levels,
  saving,
  deletingId,
  error,
  onSave,
  onDelete,
}: InvolvementScaleProps) {
  const [rows, setRows] = useState<ScaleRow[]>(() => scaleRowsOf(levels));
  /** Only has to be unique across this mount - it never leaves the page. */
  const [added, setAdded] = useState(0);
  /** The row whose inline confirmation is open, by key. One at a time. */
  const [confirming, setConfirming] = useState<string | null>(null);
  const headingId = useId();

  const blocked = scaleBlocked(rows);
  const dirty = scaleDirty(rows, levels);
  /** The rule the base enforces, mirrored so the trash can go inert (§11.5). */
  const atMinimum = rows.filter((row) => row.id !== null).length <= INVOLVEMENT_LEVELS_MIN;

  function patch(key: string, over: Partial<ScaleRow>): void {
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...over } : row)));
  }

  return (
    <section aria-labelledby={headingId} data-testid="reglages-echelle" className="space-y-4">
      <header className="space-y-1">
        <h3 id={headingId} className="font-semibold text-bleu-nuit text-lg leading-7">
          Échelle d’implication
        </h3>
        <p className="max-w-3xl text-gray-700 text-sm">
          L’échelle que le slider d’un persona parcourt. Elle n’est pas versionnée : choisir un cran
          en recopie le nom et la direction dans la version du persona. Réécrire un cran ne change
          aucune version déjà enregistrée. En revanche, un persona qui suit ce cran sans l’avoir
          redéfini recopiera cette nouvelle rédaction à sa prochaine version.
        </p>
      </header>

      <ol className="space-y-3">
        {rows.map((row, index) => {
          /*
            Narrowed ONCE, above the JSX. The confirmation below is rendered
            only for a saved row, so a second `row.id === null` test inside its
            handler would be a branch nothing can reach - and the empty string
            it fell back to would be a delete posted for the id `''` the day
            somebody relaxed the guard above it.
          */
          const savedId = row.id;
          return (
            <li key={row.key} data-testid="reglages-cran" className="space-y-2">
              <div className="flex items-start gap-2">
                {/* The position, painted beside the row rather than typed: it is
                    the arrows that move a cran, and a number an operator could
                    edit would be a second way to say the same thing. */}
                <span
                  aria-hidden="true"
                  className="mt-2 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gray-100 font-semibold text-gray-600 text-sm"
                >
                  {index + 1}
                </span>

                <div className="min-w-0 flex-1 space-y-2">
                  <ReferenceField
                    label="Nom du cran"
                    testId="reglages-cran-nom"
                    value={row.name}
                    max={INVOLVEMENT_LEVEL_NAME_MAX}
                    placeholder="en retrait"
                    onChange={(next) => patch(row.key, { name: next })}
                  />
                  <ReferenceArea
                    label="Direction d’IA du cran"
                    testId="reglages-cran-direction"
                    value={row.direction}
                    max={INVOLVEMENT_LEVEL_DIRECTION_MAX}
                    placeholder="Comment se comporter à ce degré d’implication."
                    onChange={(next) => patch(row.key, { direction: next })}
                  />
                </div>

                <div className="flex shrink-0 flex-col gap-1">
                  <IconButton
                    label={`Monter le cran ${row.name || index + 1}`}
                    disabled={index === 0}
                    onClick={() => setRows((current) => moveRow(current, index, -1))}
                  >
                    <ArrowUp size={16} strokeWidth={2} aria-hidden="true" />
                  </IconButton>
                  <IconButton
                    label={`Descendre le cran ${row.name || index + 1}`}
                    disabled={index === rows.length - 1}
                    onClick={() => setRows((current) => moveRow(current, index, 1))}
                  >
                    <ArrowDown size={16} strokeWidth={2} aria-hidden="true" />
                  </IconButton>
                  <IconButton
                    label={`Supprimer le cran ${row.name || index + 1}`}
                    tone="danger"
                    testId="reglages-cran-supprimer"
                    // A row that was never saved has nothing to delete on the
                    // server, so it just leaves the block - and the minimum does
                    // not apply to it, since the base never counted it.
                    disabled={savedId !== null && atMinimum}
                    onClick={() => {
                      if (savedId === null) {
                        setRows((current) => current.filter((one) => one.key !== row.key));
                        return;
                      }
                      setConfirming(row.key);
                    }}
                  >
                    <Trash2 size={16} strokeWidth={2} aria-hidden="true" />
                  </IconButton>
                </div>
              </div>

              {confirming === row.key && savedId !== null ? (
                <DeleteConfirm
                  testId="reglages-cran-confirmation"
                  label={`Confirmer la suppression du cran « ${row.name} »`}
                  busy={deletingId === savedId}
                  indented
                  onCancel={() => setConfirming(null)}
                  onConfirm={() => {
                    onDelete(savedId);
                    setConfirming(null);
                  }}
                >
                  Supprimer « {row.name} » ? {levelDeletionSentence(row.personaCount)}
                  {dirty
                    ? ' La suppression relit l’échelle : les modifications de ce bloc qui ne sont pas enregistrées seront perdues.'
                    : ''}
                </DeleteConfirm>
              ) : null}
            </li>
          );
        })}
      </ol>

      <div className="flex items-start gap-2">
        <span aria-hidden="true" className="block w-8 shrink-0" />
        <button
          type="button"
          data-testid="reglages-cran-ajouter"
          onClick={() => {
            setRows((current) => [...current, newScaleRow(added)]);
            setAdded((count) => count + 1);
          }}
          className="flex h-13 flex-1 cursor-pointer items-center justify-center gap-2 rounded-md border-2 border-gray-300 border-dashed bg-white font-semibold text-base text-bleu-nuit hover:bg-gray-50"
        >
          <Plus size={18} strokeWidth={2} aria-hidden="true" />
          Ajouter un cran
        </button>
        <Gutter />
      </div>

      <Callout tone="neutral">
        <p>
          Deux crans au minimum : la suppression refuse d’en laisser un seul. Les noms sont uniques.
          L’échelle n’est pas versionnée : l’enregistrement écrase, et il n’y a pas d’historique à
          remonter.
        </p>
      </Callout>

      <SectionFooter
        testId="reglages-echelle-enregistrer"
        label="Enregistrer l’échelle"
        blocked={blocked}
        dirty={dirty}
        saving={saving}
        error={error}
        onSave={() => onSave(rows)}
      />
    </section>
  );
}
